import { useId, useState, type InputHTMLAttributes } from 'react';

/**
 * Champ de mot de passe, avec un bouton afficher / masquer.
 *
 * Sans lui, une lettre de trop se paie d'un aller-retour complet : envoyer, lire
 * « identifiants incorrects », recommencer à l'aveugle. Le coût est le plus élevé
 * exactement là où il faut l'éviter — sur un téléphone, au clavier tactile.
 *
 * Trois détails qui font la différence entre ce bouton et une case à cocher décorative :
 *
 * - il est **dans** le champ, à droite, avec 44 px de cible malgré ses 18 px de dessin ;
 * - son état est annoncé (`aria-pressed`) **et** son libellé change : « Afficher le mot de
 *   passe » / « Masquer le mot de passe ». Un lecteur d'écran doit savoir ce que le bouton
 *   fera, pas seulement qu'il existe ;
 * - il repasse en masqué à chaque montage. Un mot de passe resté en clair sur un écran
 *   qu'on quitte est la seule chose que ce bouton pourrait faire de mal.
 */
export function ChampMotDePasse({
  label,
  aide,
  erreur,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label: string;
  aide?: string;
  erreur?: string;
}) {
  const id = useId();
  const aideId = `${id}-aide`;
  const erreurId = `${id}-erreur`;
  const [visible, setVisible] = useState(false);

  const decrit = [aide ? aideId : null, erreur ? erreurId : null].filter(Boolean).join(' ');

  return (
    <div className="champ champ-mot-de-passe">
      <label htmlFor={id}>{label}</label>

      <div className="champ-mot-de-passe-boite">
        <input
          id={id}
          {...props}
          type={visible ? 'text' : 'password'}
          aria-describedby={decrit || undefined}
          aria-invalid={erreur ? true : undefined}
        />
        <button
          type="button"
          className="champ-mot-de-passe-oeil"
          aria-pressed={visible}
          aria-label={visible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
          onClick={() => setVisible((etat) => !etat)}
        >
          {visible ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12Z" />
              <circle cx="12" cy="12" r="2.6" />
              <path d="m4 20 16-16" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12Z" />
              <circle cx="12" cy="12" r="2.6" />
            </svg>
          )}
        </button>
      </div>

      {aide ? (
        <p className="aide" id={aideId}>
          {aide}
        </p>
      ) : null}
      {erreur ? (
        <p className="erreur" id={erreurId}>
          {erreur}
        </p>
      ) : null}
    </div>
  );
}
